import { useState } from 'react';
import { DEFAULT_STAFF, formatPence } from '@london-life/contracts';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { actionKey, londonWhen } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

// Staff, as Lagos Life's 🧹 Staff: who works for you and what they cost each week, and the people for hire (the
// first part-week paid now, then wages every payday with the rent)
export function StaffScreen(props: PhoneAppProps) {
  const content = useCatalogue()?.staff ?? DEFAULT_STAFF;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.staff.mine(), props);
  const [leaving, setLeaving] = useState<string | null>(null);
  const roleOf = (id: string) => content.roles.find((role) => role.id === id);
  return (
    <PhoneAppFrame
      id="staff"
      title="🧹 Staff"
      className="staff-screen"
      closeLabel="Close Staff"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Checking who is in…'}
    >
      {reply && (
        <div className="business-detail">
          <section className="staff-summary" aria-label="Your staff">
            <span aria-hidden="true">🏡</span>
            <span>
              <strong>{reply.staff.length ? `${reply.staff.length} on the payroll` : 'No staff yet'}</strong>
              <small>
                {reply.staff.length
                  ? `${formatPence(reply.weeklyPence)} a week · next payday ${londonWhen(reply.paydayAt)}`
                  : 'Hire help for the house. The first part-week is paid when they start, then wages come off every payday with the rent.'}
              </small>
            </span>
          </section>
          {reply.staff.length > 0 && (
            <ul className="staff-list" aria-label="Who works for you">
              {reply.staff.map((member) => {
                const role = roleOf(member.role);
                if (!role) return null;
                return (
                  <li key={member.id}>
                    <span aria-hidden="true">{role.emoji}</span>
                    <span>
                      <strong>
                        {role.label} · {role.name}
                      </strong>
                      <small>
                        {formatPence(member.wagePenceWeekly)} a week · paid till {londonWhen(member.paidTo)}
                      </small>
                      <em>✨ {role.perk}</em>
                    </span>
                    {leaving === member.id ? (
                      <div className="jet-sell">
                        <span>Let {role.name} go? Wages already paid are not given back.</span>
                        <button type="button" onClick={() => setLeaving(null)} disabled={busy}>
                          Keep
                        </button>
                        <button
                          type="button"
                          className="jet-sell-yes"
                          disabled={busy}
                          onClick={() => void run(() => api.staff.letGo(member.id)).then(() => setLeaving(null))}
                        >
                          Let go
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="flights-terminal" onClick={() => setLeaving(member.id)}>
                        Let go…
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {reply.offers.length > 0 && <strong className="business-step-title">FOR HIRE</strong>}
          <ul className="staff-list" aria-label="People for hire">
            {reply.offers.map((offer) => {
              const role = roleOf(offer.role);
              if (!role) return null;
              const short = reply.walletPence < offer.firstPence;
              return (
                <li key={role.id} className={offer.blocked ? 'blocked' : ''}>
                  <span aria-hidden="true">{role.emoji}</span>
                  <span>
                    <strong>
                      {role.label} · {role.name}
                    </strong>
                    <small>
                      {formatPence(role.wagePenceWeekly)} a week{role.livesIn ? ' · lives in' : ''}
                    </small>
                    <p>{role.blurb}</p>
                    <em>✨ {role.perk}</em>
                    {offer.blocked ? (
                      <small className="staff-blocked">🔒 {offer.blocked}</small>
                    ) : (
                      <button
                        type="button"
                        className="city-card-go"
                        disabled={busy || short}
                        onClick={() => void run(() => api.staff.hire(role.id, actionKey('staff')))}
                      >
                        {short
                          ? `Need ${formatPence(offer.firstPence)}`
                          : `Hire · ${formatPence(offer.firstPence)} till payday`}
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="business-muted">
            Wages already paid for the week are not given back when someone leaves. Staff stay in your home while you
            travel. Moving to a smaller home can mean staff have to go.
          </p>
        </div>
      )}
    </PhoneAppFrame>
  );
}
